import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useLocation } from 'react-router-dom'
import { FormattedMessage, injectIntl } from 'react-intl'
import {
  MdClose,
  MdDelete,
  MdOpenInFull,
  MdCloseFullscreen,
} from 'react-icons/md'
import { FaRobot } from 'react-icons/fa'
import {
  sendChatMessage,
  clearChatHistory,
  toggleChatWindow,
  setChatWindowOpen,
  setChatFullscreen,
  loadConversations,
  deleteConversation,
  resetChatScope,
} from './actions'
import {
  selectChatMessages,
  selectChatLoading,
  selectChatError,
  selectChatWindowOpen,
  selectConversationId,
  selectChatStatus,
  selectChatFullscreen,
  selectChatScopeCode,
  selectConversationsLoading,
  selectDeletingConversationId,
} from './selectors'
import {
  ChatMessage,
  SourcesList,
  StatusIndicator,
  SuggestedQuestions,
  STATUS_MESSAGE_MAP,
  ChatInputForm,
  ConversationHistory,
} from './components'
import messages from './messages'
import styles from './styles.module.scss'
import { TOKEN_LIMIT_EXCEEDED } from './constants'
import {
  buildChatContext,
  getChatScopeCode,
  resolveChatErrorMessage,
} from './utils'

const ChatWindow = ({ intl }) => {
  const { companyCode, customerCode } = useParams()
  const location = useLocation()
  const dispatch = useDispatch()
  const chatMessages = useSelector(selectChatMessages)
  const isLoading = useSelector(selectChatLoading)
  const error = useSelector(selectChatError)
  const isOpen = useSelector(selectChatWindowOpen)
  const isFullscreen = useSelector(selectChatFullscreen)
  const conversationId = useSelector(selectConversationId)
  const statusMessage = useSelector(selectChatStatus)
  const storedScopeCode = useSelector(selectChatScopeCode)
  const conversationsLoading = useSelector(selectConversationsLoading)
  const deletingConversationId = useSelector(selectDeletingConversationId)
  const scopeCode = getChatScopeCode({ customerCode, companyCode })
  const isDeletingActiveConversation =
    Boolean(conversationId) && deletingConversationId === conversationId
  const chatContext = buildChatContext({
    location,
    customerCode,
    companyCode,
    chatSurface: isFullscreen ? 'page' : 'floating',
    documentTitle: typeof document !== 'undefined' ? document.title : null,
  })

  const [inputMessage, setInputMessage] = useState('')
  const [width, setWidth] = useState(380)
  const [height, setHeight] = useState(600)
  const [isResizing, setIsResizing] = useState(null)
  const messagesEndRef = useRef(null)
  const fullscreenEntryKeyRef = useRef(null)

  const startResizing = useCallback(
    (type) => (e) => {
      e.preventDefault()
      setIsResizing(type)
    },
    []
  )

  const stopResizing = useCallback(() => {
    setIsResizing(null)
  }, [])

  const resize = useCallback(
    (e) => {
      if (isResizing) {
        if (isResizing === 'width' || isResizing === 'both') {
          const newWidth = window.innerWidth - e.clientX - 20
          if (newWidth >= 300 && newWidth <= 1200) {
            setWidth(newWidth)
          }
        }
        if (isResizing === 'height' || isResizing === 'both') {
          const newHeight = window.innerHeight - e.clientY - 20
          if (newHeight >= 200 && newHeight <= window.innerHeight - 40) {
            setHeight(newHeight)
          }
        }
      }
    },
    [isResizing]
  )

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', resize)
      window.addEventListener('mouseup', stopResizing)
    }
    return () => {
      window.removeEventListener('mousemove', resize)
      window.removeEventListener('mouseup', stopResizing)
    }
  }, [isResizing, resize, stopResizing])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages.size, statusMessage, isOpen, isFullscreen])

  // Reset chat state when navigating to a different scope in floating mode.
  //
  // The stored scope is written only by RESET_CHAT_SCOPE, and of its three dispatchers only this one
  // used to skip a null stored scope. In a session that never opens the chat page or fullscreen — the
  // floating widget alone — nothing therefore ever wrote the scope, this guard could never become
  // true, and switching company left the previous company's messages on screen under the new
  // company's heading. Resetting an initially empty store costs nothing, so the scope is initialized
  // here instead.
  useEffect(() => {
    if (isFullscreen) return
    if (storedScopeCode !== scopeCode) {
      dispatch(resetChatScope(scopeCode))
    }
  }, [scopeCode]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load conversation history when entering fullscreen mode
  useEffect(() => {
    if (isFullscreen) {
      fullscreenEntryKeyRef.current = location.key
    } else {
      fullscreenEntryKeyRef.current = null
    }
    if (!isFullscreen) return

    // The reset stays conditional, the load does not. Once the floating effect above started
    // initializing storedScopeCode on mount, the scope already matched by the time the user expanded
    // to fullscreen — so this whole block was skipped and the history stayed empty until a company
    // switch happened to run it. This is the only place the floating widget loads the list.
    if (storedScopeCode !== scopeCode) {
      dispatch(resetChatScope(scopeCode))
    }

    if (scopeCode) {
      dispatch(loadConversations(scopeCode))
    }
  }, [isFullscreen, scopeCode]) // eslint-disable-line react-hooks/exhaustive-deps

  // Close fullscreen on any React Router navigation — including clicks to the current page.
  // location.key changes on every React Router navigation but NOT on our own window.history.pushState
  // or on the back-button popstate (which restores the original key), so this only fires on genuine
  // user-initiated navigation.
  useEffect(() => {
    if (!isFullscreen) return
    if (
      fullscreenEntryKeyRef.current !== null &&
      location.key !== fullscreenEntryKeyRef.current
    ) {
      dispatch(setChatFullscreen(false))
    }
  }, [location.key]) // eslint-disable-line react-hooks/exhaustive-deps

  // Close fullscreen when the user presses the browser back button.
  // The minimize button (⊠) dispatches setChatFullscreen(false) first, which causes
  // this effect to re-run and remove the listener before history.back() fires its
  // popstate — so the listener is only reached by a genuine browser back press.
  useEffect(() => {
    if (!isFullscreen) return
    const handlePopState = () => {
      dispatch(setChatFullscreen(false))
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [isFullscreen, dispatch])

  const handleSendMessage = (e) => {
    e.preventDefault()
    if (inputMessage.trim() && !isLoading) {
      dispatch(
        sendChatMessage(
          inputMessage.trim(),
          conversationId,
          scopeCode,
          chatContext
        )
      )
      setInputMessage('')
    }
  }

  const handleClearHistory = () => {
    // Guarded as well as disabled: clearing mid-stream would leave the saga running to restore the
    // conversation, and deleting one races the backend persisting the assistant message into it.
    if (isLoading) return
    if (isFullscreen && conversationId) {
      dispatch(deleteConversation(conversationId, scopeCode))
    } else {
      dispatch(clearChatHistory())
    }
  }

  const handleToggle = () => {
    dispatch(toggleChatWindow())
  }

  const handleExpandToPage = () => {
    if (scopeCode) {
      dispatch(setChatWindowOpen(false))
      dispatch(setChatFullscreen(true))
      // Push a history entry so the browser back button closes fullscreen
      window.history.pushState({ chatFullscreen: true }, '')
    }
  }

  const handleMinimizeFromFullscreen = () => {
    // Dispatch state changes first — this causes the popstate listener to be removed
    // (useEffect cleanup runs on re-render) before history.back() fires its event.
    // So the popstate handler is NOT triggered by this call, only by the browser back button.
    dispatch(setChatFullscreen(false))
    dispatch(setChatWindowOpen(true))
    if (window.history.state?.chatFullscreen) {
      window.history.back()
    }
  }

  const handleSuggestedQuestionClick = (question) => {
    if (!isLoading) {
      dispatch(
        sendChatMessage(question, conversationId, scopeCode, chatContext)
      )
    }
  }

  if (!isOpen && !isFullscreen) return null
  if (!isFullscreen && location.pathname.endsWith('/chat')) return null

  const initialQuestions = [
    messages.suggestedQuestion1,
    messages.suggestedQuestion2,
    messages.suggestedQuestion3,
  ]

  const RenderError = () => {
    if (error?.code === TOKEN_LIMIT_EXCEEDED) {
      const resetDate = error?.data?.resetDate
        ? new Date(error.data.resetDate).toLocaleDateString('fi-FI', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })
        : null

      return (
        <div className={styles.errorMessage}>
          {intl.formatMessage(messages.tokenLimitExceeded)}
          {resetDate && (
            <div>
              {intl.formatMessage(messages.tokenResetDate, { resetDate })}
            </div>
          )}
        </div>
      )
    }

    return (
      <div className={styles.errorMessage}>
        {resolveChatErrorMessage(intl, error)}
      </div>
    )
  }

  return (
    <div
      className={`${styles.chatContainer} ${
        isResizing ? styles.isResizing : ''
      } ${isFullscreen ? styles.chatContainerFullscreen : ''}`}
      style={isFullscreen ? {} : { width: `${width}px`, height: `${height}px` }}
    >
      {!isFullscreen && (
        <>
          <div
            className={styles.resizeHandleLeft}
            onMouseDown={startResizing('width')}
          />
          <div
            className={styles.resizeHandleTop}
            onMouseDown={startResizing('height')}
          />
          <div
            className={styles.resizeHandleCorner}
            onMouseDown={startResizing('both')}
          />
        </>
      )}
      <div className={isFullscreen ? styles.chatPageHeader : styles.chatHeader}>
        <div className={styles.chatTitle}>
          <FaRobot className={styles.chatIcon} />
          <span>
            <FormattedMessage {...messages.assistant} />
          </span>
        </div>
        <div className={styles.chatHeaderActions}>
          {isFullscreen && (
            <ConversationHistory
              scopeCode={scopeCode}
              companyCode={companyCode}
            />
          )}
          {(isFullscreen ? chatMessages.size > 0 : chatMessages.size > 0) && (
            <button
              className={styles.headerButton}
              onClick={handleClearHistory}
              disabled={isDeletingActiveConversation || isLoading}
              aria-busy={isDeletingActiveConversation}
              aria-label={intl.formatMessage(messages.clearHistory)}
              title={intl.formatMessage(messages.clearHistory)}
            >
              {isDeletingActiveConversation ? (
                <span
                  className={styles.headerButtonSpinner}
                  aria-hidden="true"
                />
              ) : (
                <MdDelete size={20} />
              )}
            </button>
          )}
          {isFullscreen ? (
            <button
              className={styles.headerButton}
              onClick={handleMinimizeFromFullscreen}
              aria-label={intl.formatMessage(messages.backToFloating)}
              title={intl.formatMessage(messages.backToFloating)}
            >
              <MdCloseFullscreen size={20} />
            </button>
          ) : (
            <>
              {scopeCode && (
                <button
                  className={styles.headerButton}
                  onClick={handleExpandToPage}
                  aria-label={intl.formatMessage(messages.openFullPage)}
                  title={intl.formatMessage(messages.openFullPage)}
                >
                  <MdOpenInFull size={18} />
                </button>
              )}
              <button
                className={styles.headerButton}
                onClick={handleToggle}
                aria-label={intl.formatMessage(messages.close)}
                title={intl.formatMessage(messages.close)}
              >
                <MdClose size={20} />
              </button>
            </>
          )}
        </div>
      </div>
      <>
        <div
          className={
            isFullscreen ? styles.chatPageMessages : styles.chatMessages
          }
        >
          {chatMessages.size === 0 && isFullscreen && conversationsLoading ? (
            <div className={styles.chatPageLoadingState}>
              <div className={styles.conversationsLoadingSpinner} />
            </div>
          ) : chatMessages.size === 0 ? (
            <div
              className={
                isFullscreen ? styles.chatPageEmptyState : styles.emptyState
              }
            >
              <FaRobot
                size={isFullscreen ? 64 : 48}
                className={styles.emptyStateIcon}
              />
              <p>
                <FormattedMessage {...messages.welcomeMessage} />
              </p>
              <div className={styles.disclaimer}>
                <FormattedMessage {...messages.aiDisclaimer} />
              </div>
              <div className={styles.suggestedQuestions}>
                {initialQuestions.map((question, index) => (
                  <button
                    key={index}
                    className={styles.suggestedQuestionButton}
                    onClick={() =>
                      handleSuggestedQuestionClick(intl.formatMessage(question))
                    }
                  >
                    <FormattedMessage {...question} />
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {chatMessages.map((message, index) => (
            <ChatMessage key={index} message={message} />
          ))}
          {/* Rendered unconditionally: it is the aria-live region, and it has to outlive the
              individual statuses for a screen reader to announce them. */}
          <StatusIndicator
            status={
              isLoading && statusMessage
                ? STATUS_MESSAGE_MAP[statusMessage]
                  ? intl.formatMessage(STATUS_MESSAGE_MAP[statusMessage])
                  : statusMessage
                : null
            }
          />
          {error && <RenderError />}
          {!isLoading && <SourcesList chatMessages={chatMessages} />}
          {!isLoading && (
            <SuggestedQuestions
              chatMessages={chatMessages}
              onQuestionClick={handleSuggestedQuestionClick}
            />
          )}
          <div ref={messagesEndRef} />
        </div>
        <ChatInputForm
          inputMessage={inputMessage}
          onInputChange={(e) => setInputMessage(e.target.value)}
          onSubmit={handleSendMessage}
          isLoading={isLoading}
          formClassName={
            isFullscreen ? styles.chatPageInputForm : styles.chatInputForm
          }
        />
      </>
    </div>
  )
}

export default injectIntl(ChatWindow)
